import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, MessageCircle, Mail, MapPin, Loader2 } from "lucide-react";
import { BRAND } from "../../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll reply soon — promise.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't send. Please try again or WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.3em] text-mo-terracotta mb-4">
            — Say Hello
          </div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-mo-ink leading-[1.05]">
            Questions, recipes, <span className="italic text-mo-green">a chai chat?</span>
          </h2>
          <p className="mt-6 text-mo-ink-soft max-w-md">
            We read every message. Whether it's an order, a recipe idea, or
            feedback on the pinch of haldi in your daal — write in.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href={BRAND.whatsapp.link}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-center gap-4 p-5 rounded-2xl border border-[#5C5248]/15 hover:border-[#25D366] transition-colors duration-300 group"
            >
              <div className="w-12 h-12 rounded-full bg-[#25D366]/15 flex items-center justify-center text-[#25D366] group-hover:scale-110 transition-transform duration-300">
                <MessageCircle size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-mo-ink-soft">WhatsApp</div>
                <div className="font-medium text-mo-ink">{BRAND.whatsapp.number}</div>
              </div>
            </a>

            <a
              href={BRAND.instagram.link}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-instagram"
              className="flex items-center gap-4 p-5 rounded-2xl border border-[#5C5248]/15 hover:border-[#B33922] transition-colors duration-300 group"
            >
              <div className="w-12 h-12 rounded-full bg-[#B33922]/15 flex items-center justify-center text-mo-terracotta group-hover:scale-110 transition-transform duration-300">
                <Instagram size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-mo-ink-soft">Instagram</div>
                <div className="font-medium text-mo-ink">{BRAND.instagram.handle}</div>
              </div>
            </a>

            <div className="flex items-center gap-4 p-5 rounded-2xl border border-[#5C5248]/15">
              <div className="w-12 h-12 rounded-full bg-[#2A4B3C]/15 flex items-center justify-center text-mo-green">
                <MapPin size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-mo-ink-soft">Made in</div>
                <div className="font-medium text-mo-ink">Karnataka, India</div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-[#EAE3D9]/60 rounded-3xl p-8 sm:p-10 border border-[#5C5248]/12 kraft-grain"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field
              label="Name"
              value={form.name}
              onChange={onChange("name")}
              required
              testId="contact-input-name"
            />
            <Field
              label="Email"
              type="email"
              value={form.email}
              onChange={onChange("email")}
              required
              testId="contact-input-email"
            />
          </div>
          <div className="mt-5">
            <label className="block text-xs uppercase tracking-[0.2em] text-mo-ink-soft mb-2">
              Your message
            </label>
            <textarea
              rows={5}
              value={form.message}
              onChange={onChange("message")}
              required
              data-testid="contact-input-message"
              className="w-full px-4 py-3 rounded-lg border border-[#5C5248]/30 bg-[#F9F6F0] text-mo-ink focus:outline-none focus:ring-2 focus:ring-[#B33922]/40 focus:border-[#B33922]/60 resize-none"
              placeholder="Ask us anything…"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="mt-7 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-mo-ink text-mo-cream font-medium hover:bg-mo-terracotta transition-all duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />}
            Send message
          </button>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-mo-ink-soft mb-2">
      {label}
    </span>
    <input
      {...props}
      data-testid={testId}
      className="w-full px-4 py-3 rounded-lg border border-[#5C5248]/30 bg-[#F9F6F0] text-mo-ink focus:outline-none focus:ring-2 focus:ring-[#B33922]/40 focus:border-[#B33922]/60"
    />
  </label>
);
